import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ChevronDown, Search } from "lucide-react";

import { CtaBand } from "@/components/page-parts";
import { FIRM } from "@/lib/firm";
import { GUIDES } from "@/lib/guides";
import { CATEGORY_HERO } from "@/lib/guide-visuals";
import guidesHero from "@/assets/guides-hero.jpg";
import { useWebMCP } from "use-webmcp-tool";
import { GUIDE_CATEGORIES, guideLinkText, searchPublicGuides, validateGuideFilter, type GuideCategoryFilter } from "@/lib/public-guide-search";
import { runWebMCPTool } from "@/lib/webmcp-telemetry";

export const Route = createFileRoute("/guides/")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { title: fitTitle(`Queensland Property & Estate Law Guides | ${FIRM.name}`) },
      {
        name: "description",
        content:
          fitDescription("Plain-English guides to Queensland conveyancing, wills, probate and powers of attorney — written by Sunshine Coast lawyers for people who are not lawyers."),
      },
      { property: "og:title", content: fitTitle(`Guides | ${FIRM.name}`) },
      {
        property: "og:description",
        content: fitDescription("Plain-English guides to Queensland property and estate law."),
      },
      { property: "og:url", content: "https://coastsidelaw.com.au/guides" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/guides" }],
  }),
  component: GuidesIndex,
});

function GuidesIndex() {
  const categories = GUIDE_CATEGORIES;
  const [query, setQuery] = useState("");
  const [active, setActive] = useState<(typeof categories)[number] | "All">("All");

  const filtered = useMemo(() => {
    return searchPublicGuides(query, active);
  }, [query, active]);

  function applyFilter(nextQuery: string, nextCategory: GuideCategoryFilter) {
    validateGuideFilter(nextQuery, nextCategory);
    setQuery(nextQuery);
    setActive(nextCategory);
  }

  useWebMCP({
    name: "read_visible_guides",
    description: "Read up to ten guide links in the current search and category view.",
    annotations: { readOnlyHint: true },
    execute: () => runWebMCPTool("read_visible_guides", () => guideLinkText(filtered)),
  });
  useWebMCP({
    name: "filter_guides",
    description: "Change the guide search and category using the same filters as the visible controls.",
    inputSchema: { type: "object", properties: {
      query: { type: "string", maxLength: 200, description: "Search words; empty text clears the search." },
      category: { type: "string", enum: ["All", ...GUIDE_CATEGORIES], description: "Guide category to display." },
    }, required: ["query", "category"], additionalProperties: false },
    execute: ({ query: nextQuery, category }: { query: string; category: GuideCategoryFilter }) => runWebMCPTool("filter_guides", () => {
      applyFilter(nextQuery, category);
      return "Guide filters updated.";
    }),
  });

  return (
    <>
      <section className="relative isolate overflow-hidden border-b border-border">
        <img
          src={guidesHero}
          alt="Caloundra coastline on the Sunshine Coast with the Glass House Mountains behind"
          width={1920}
          height={768}
          fetchPriority="high"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-teal/90 via-brand-teal/50 to-transparent" />
        <div className="relative mx-auto max-w-6xl px-5 py-16 sm:py-24">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-foreground/80">Guides</p>
            <h1 className="mt-3 font-serif text-4xl leading-tight text-primary-foreground sm:text-5xl">
              Queensland law, explained in plain English
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-primary-foreground/90">
              The questions clients actually ask, answered properly. General information about Queensland law — not advice about your situation, which needs a conversation.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="mb-12">
          <label className="relative block">
            <span className="sr-only">Search the guides</span>
            <Search
              className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => applyFilter(e.target.value, active)}
              maxLength={200}
              placeholder="Search guides — probate, cooling off, granny flat…"
              className="w-full rounded-sm border border-border bg-card py-3 pl-11 pr-4 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-accent"
            />
          </label>
          <div className="mt-4 flex flex-wrap gap-2">
            {(["All", ...categories] as const).map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => applyFilter(query, cat)}
                aria-pressed={active === cat}
                className={
                  active === cat
                    ? "rounded-sm border border-primary bg-primary px-3.5 py-2 text-xs font-semibold text-primary-foreground"
                    : "rounded-sm border border-border bg-card px-3.5 py-2 text-xs font-semibold text-muted-foreground transition-colors hover:border-accent hover:text-foreground"
                }
              >
                {cat}
              </button>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Showing {filtered.length} of {GUIDES.length} guides
          </p>
          <p className="mt-3 text-xs text-muted-foreground">
            Looking at a granny flat arrangement?{" "}
            <Link
              to="/granny-flat-resources"
              className="font-semibold text-primary underline"
            >
              See all our granny flat guides and checklists
            </Link>
            .
          </p>
        </div>

        {filtered.length === 0 && (
          <p className="rounded-sm border border-border bg-card p-6 text-sm leading-relaxed text-muted-foreground">
            No guides match that search. Try a different word, or{" "}
            <Link to="/contact" className="font-semibold text-primary underline">
              ask us directly
            </Link>{" "}
            — if it is a question clients ask, we will write it up.
          </p>
        )}

        {categories.map((cat) => {
          const items = filtered.filter((g) => g.category === cat);
          if (items.length === 0) return null;
          return (
            <details
              key={cat + (query ? "-q" : "")}
              open={query ? true : undefined}
              className="group/cat mb-6 overflow-hidden rounded-sm border border-border bg-card shadow-sm"
            >
              <summary className="relative flex cursor-pointer list-none items-end overflow-hidden [&::-webkit-details-marker]:hidden">
                <img
                  src={CATEGORY_HERO[cat].src}
                  alt={CATEGORY_HERO[cat].alt}
                  loading="lazy"
                  width={1600}
                  height={912}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-brand-teal/90 via-brand-teal/60 to-transparent" />
                <div className="relative flex w-full items-center justify-between gap-4 px-6 py-10 sm:px-8 sm:py-14">
                  <div>
                    <h2 className="font-serif text-3xl text-primary-foreground sm:text-4xl">{cat}</h2>
                    <p className="mt-1 text-sm font-semibold text-primary-foreground/90">
                      {items.length} {items.length === 1 ? "guide" : "guides"} · tap to open
                    </p>
                  </div>
                  <ChevronDown className="size-8 shrink-0 text-primary-foreground transition-transform group-open/cat:rotate-180" />
                </div>
              </summary>
              <div className="space-y-4 p-4 sm:p-6">
                {items.map((g) => (
                  <Link
                    key={g.slug}
                    to="/guides/$slug"
                    params={{ slug: g.slug }}
                    className="group block rounded-sm border border-border bg-background p-6 card-elevate"
                  >
                    <h3 className="font-serif text-xl leading-snug text-foreground group-hover:underline">
                      {g.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {g.description}
                    </p>
                  </Link>
                ))}
              </div>
            </details>
          );
        })}
      </section>

      <CtaBand title="Need advice on your own situation?" />
      <div className="h-20" />
    </>
  );
}
